import { SCHEMA } from '@vedalib/editor/lib/brand'
import type { ObjectFitEnum } from '@vedalib/editor/lib/files'
import React, { useMemo } from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import {
  FieldColorPicker,
  FieldSegmented,
  FieldSelect,
  FieldSlider,
  FormField,
} from 'components/formKit'
import { Typography } from 'components/uiKit/Typography'
import { BrandFieldShadow } from 'services/Branding/components'
import { BORDER_OPTIONS, OBJECT_FIT_OPTIONS } from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { t } from 'services/Translation'

import type { ImageFormBrandType } from './imageTypes'

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.image.style, {
  image: {
    objectFit: genBrandField({
      as: FieldSelect<ObjectFitEnum>,
      label: t('input.label.objectFit'),
      props: { options: OBJECT_FIT_OPTIONS, fluid: true },
    }),
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.image.schema.boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
})

const ImageFormStyle: ImageFormBrandType = ({ device, subConfigs }) => {
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom gap sides>
        <FormField config={schema.image.objectFit} />
        <FormField config={schema.image.borderRadius} />
        <FormField config={schema.image.borderStyle} />
        <FormField config={schema.image.borderColor} />
        <FormField config={schema.image.borderWidth} />

        <Typography weight='medium'>{t('style.effects')}</Typography>
        <FormField config={schema.effects.boxShadow} />
      </Padding>
    </LayoutScroll>
  )
}

export default React.memo(ImageFormStyle) as typeof ImageFormStyle
